import Grid from "@mui/material/Grid";
import { styled, useTheme } from "@mui/material/styles";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { grey } from "designSystem/styles/color";
import { STANDARD_BORDER_STYLE } from "designSystem/styles/style-functions";
import emojiToolkit from "emoji-toolkit";
import { parse, SLACK } from "c8-markdown-parser";
import React from "react";
import * as api from "../api";
import Markdown from "./Markdown";
import SlackContext from "./SlackContext";

const PREFIX = "Attachment";

const classes = {
	block: `${PREFIX}-block`,
	imageBlock: `${PREFIX}-imageBlock`,
	section: `${PREFIX}-section`,
	sectionAccessory: `${PREFIX}-sectionAccessory`,
	sectionField: `${PREFIX}-sectionField`,
	sectionFields: `${PREFIX}-sectionFields`,
};

const StyledGrid = styled(Grid)(({
	theme,
}) => ({
	[`& .${classes.block}`]: {
		marginBottom: theme.spacing(1.5),
		"&:last-child": {
			marginBottom: 0,
		},
	},
	[`& .${classes.imageBlock}`]: {
		cursor: "zoom-in",
		width: "100%",
		"& img": {
			maxWidth: "100%",
			borderRadius: "4px",
			boxShadow: "inset 0 0 0 1px rgba(0,0,0,.1)",
		},
	},
	[`& .${classes.section}`]: {
		flex: 1,
		"& $imageElement": {
			height: "88px",
			width: "88px",
		},
	},
	[`& .${classes.sectionAccessory}`]: {
		"& $imageElement": {
			height: "88px",
			width: "88px",
		},
	},
	[`& .${classes.sectionField}`]: {
		flexBasis: "50%",
		padding: theme.spacing(2),
	},
	[`& .${classes.sectionFields}`]: {
		display: "flex",
		flexWrap: "wrap",
	},
}));

const skinToneShortnameToCodePoint = {
	":skin-tone-2:": "\u{1F3FB}",
	":skin-tone-3:": "\u{1F3FC}",
	":skin-tone-4:": "\u{1F3FD}",
	":skin-tone-5:": "\u{1F3FE}",
	":skin-tone-6:": "\u{1F3FF}",
};

function name2emoji(name: string): string {
	const result: string = emojiToolkit.shortnameToUnicode(`:${name}:`);

	// replace 'skin-tone' shortnames with corresponding Unicode code point
	return result.replace(/:skin-tone-\d:/g, (skinTone) => {
		const codePoint = skinToneShortnameToCodePoint[skinTone];
		return codePoint || skinTone;
	});
}

function newline2br(text: string): JSX.Element[] {
	if (!text) {
		return null;
	}

	const items = text.split("\n");

	const elements = [<React.Fragment key="0">{items[0]}</React.Fragment>];
	for (let i = 1; i < items.length; i++) {
		elements.push(<React.Fragment key={i}><br />{items[i]}</React.Fragment>);
	}

	return elements;
}

function stylized(element: JSX.Element, style: api.RichTextSectionElement.TextStyle): JSX.Element {
	let result = element;
	if (style) {
		if (style.bold) {
			result = <b>{result}</b>;
		}
		if (style.italic) {
			result = <i>{result}</i>;
		}
		if (style.strike) {
			result = <s>{result}</s>;
		}
		if (style.code) {
			result = <code>{result}</code>;
		}
	}
	return result;
}


export const Attachment: React.FunctionComponent<api.Attachment> = (attachment) => {
	if (!attachment.blocks?.length) {
		return null;
	}
	const color = attachment.color ?? grey.A100;
	const theme = useTheme();
	return (
		<Grid
			container
			direction="row"
			sx={{
				flexWrap: "nowrap",
				width: "100%",
			}}
		>
			<Grid
				item
				sx={{
					flexShrink: 0,
					border: "solid 2px",
					borderColor: color,
					borderRadius: "2px",
					marginRight: theme.spacing(3),
					width: "4px",
				}}
			/>
			<Grid
				item
				sx={{
					width: "100%",
					flexShrink: 1,
					paddingBottom: theme.spacing(1),
					paddingTop: theme.spacing(1),	
				}}
			>
				{attachment.blocks.map((block) => <LayoutBlock key={block.blockId} {...block} />)}
			</Grid>
		</Grid>
	);
};

interface FileProps {
	file: api.File;
}

const StyledAForFile = styled("a")(({
	theme,
}) => ({
	border: STANDARD_BORDER_STYLE,
	borderRadius: "3px",
	display: "flex",
	flexDirection: "row",
	flexWrap: "nowrap",
	margin: theme.spacing(1, 0),
	overflow: "hidden",
	overflowWrap: "break-word",
	padding: theme.spacing(1),
	"& svg": {
		marginRight: theme.spacing(1),
	},
}));

export const File: React.FunctionComponent<FileProps> = ({ file }) => {
	return (
		<StyledAForFile
			href={file.urlPrivate}
			target="_blank"
		>
			<AttachFileIcon />
			<span>{file.name}</span>
		</StyledAForFile>
	);
};

interface ReactionProps {
	reaction: api.Reaction;
}

const StyledSpanForReaction = styled("span")(({
	theme,
}) => ({
	backgroundColor: grey[100],
	borderRadius: "12px",
	padding: theme.spacing(1, 1.5),
	marginRight: theme.spacing(1),
	marginTop: theme.spacing(1),
	"& span:first-child": {
		marginRight: theme.spacing(1),
	},
}));

export const Reaction: React.FunctionComponent<ReactionProps> = ({ reaction }) => {
	return (
		<StyledSpanForReaction>
			<span>{name2emoji(reaction.name)}</span>
			<span>{reaction.count}</span>
		</StyledSpanForReaction>
	);
};



export const LayoutBlock: React.FunctionComponent<api.LayoutBlock> = (block) => {
	switch (block.type) {
		case "context": return <ContextBlock {...(block as api.ContextBlock)} />;
		case "divider": return <DividerBlock {...(block as api.DividerBlock)} />;
		case "header": return <HeaderBlock {...(block as api.HeaderBlock)} />;
		case "image": return <ImageBlock {...(block as api.ImageBlock)} />;
		case "rich_text": return <RichTextBlock {...(block as api.RichTextBlock)} />;
		case "section": return <SectionBlock {...(block as api.SectionBlock)} />;
	}
	return null;
};

const StyledDivForContextBlock = styled("div")(({
	theme,
}) => ({
	display: "flex",
	flexWrap: "wrap",
	marginBottom: theme.spacing(1.5),
	"&:last-child": {
		marginBottom: 0,
	},
	"& $imageElement": {
		height: "20px",
		width: "20px",
		marginRight: theme.spacing(1),
	},
	"& $markdown": {
		marginRight: theme.spacing(4),
	},
	"& $plaintext": {
		marginRight: theme.spacing(4),
	},
}));

export const ContextBlock: React.FunctionComponent<api.ContextBlock> = (block) => {
	return (
		<StyledDivForContextBlock>
			{block.elements.map((e, i) => <BlockElement key={i} {...e} />)}
		</StyledDivForContextBlock>
	);
};

export const DividerBlock: React.FunctionComponent<api.DividerBlock> = (props) => {
	return (
		<hr
			style={{
				border: 0,
				borderTop: STANDARD_BORDER_STYLE,
				width: "100%",
			}}
		/>
	);
};

export const HeaderBlock: React.FunctionComponent<api.HeaderBlock> = (props) => {
	return (
		<div
			style={{
				fontWeight: "bold",
				fontSize: "16px",
			}}
		>
			<TextObject {...props.text} />
		</div>
	);
};

export const ImageBlock: React.FunctionComponent<api.ImageBlock> = (props) => {
	return (
		<StyledGrid
			className={classes.block}
			container
			direction="column"
		>
			{props.title ? <Grid item sx={{ fontWeight: "700" }}><TextObject {...props.title} /></Grid> : null}
			<a
				className={classes.imageBlock}
				href={props.imageUrl}
				target="_blank"
				title={props.altText}
			>
				<img alt={props.altText} src={props.imageUrl} />
			</a>
		</StyledGrid>
	);
};

export const SectionBlock: React.FunctionComponent<api.SectionBlock> = (block, usersMap) => {
	return (
		<StyledGrid
			className={classes.block}
			container
			direction="column"
		>
			<Grid container direction="row">
				<Grid className={classes.section} item>
					<TextObject {...block.text} />
				</Grid>
				{
					block.accessory
						? (
							<Grid className={classes.sectionAccessory} item>
								<BlockElement {...block.accessory} />
							</Grid>
						)
						: null
				}
			</Grid>
			{block.fields?.length
				? (
					<Grid
						className={classes.sectionFields}
						container
						direction="row"
					>
						{
							block.fields.map((f, i) => (
								<Grid
									className={classes.sectionField}
									item
									key={i}
								>
									<TextObject {...f} />
								</Grid>
							))
						}
					</Grid>
				)
				: null
			}
		</StyledGrid>
	);
};

export const TextObject: React.FunctionComponent<api.TextObject> = (props) => {
	switch (props.type) {
		case "mrkdwn": return <span><Markdown markdown={(props as api.MarkdownTextObject).text} /></span>;
		case "plain_text": return <span>{(props as api.PlainTextObject).text}</span>;
	}
	return null;
};

const StyledSpan = styled("span")(({
	theme,
}) => ({
	marginBottom: theme.spacing(1.5),
	"&:last-child": {
		marginBottom: 0,
	},
}));

export const RichTextBlock: React.FunctionComponent<api.RichTextBlock> = (block) => {
	const elements = block.elements.map((element, index) => <BlockElement key={index} {...element} />);
	return (
		<StyledSpan>{elements}</StyledSpan>
	);
};

export const BlockElement: React.FunctionComponent<api.BlockElement> = (element) => {
	switch (element.type) {
		case "rich_text_section": return <RichTextSectionElement {...element as api.RichTextSectionElement} />;
		case "rich_text_list": return <RichTextListElement {...element as api.RichTextListElement} />;
		case "rich_text_quote": return <RichTextQuoteElement {...element as api.RichTextQuoteElement} />;
		case "rich_text_preformatted": return <RichTextPreformattedElement {...element as api.RichTextPreformattedElement} />;
		case "image": return <ImageElement {...(element as api.ImageElement)} />;
		case "mrkdwn": return <span><Markdown markdown={(element as api.MarkdownTextObject).text} /></span>;
	}
	return null;
};

export const RichTextSectionElement: React.FunctionComponent<api.RichTextSectionElement> = (element) => {
	const elements = element.elements.map((element, index) => <RichTextElement key={index} {...element} />);
	return <div>{elements}</div>;
};


export const RichTextListElement: React.FunctionComponent<api.RichTextListElement> = (element) => {
	const elements = element.elements.map((element, index) => <li key={index}><RichTextElement {...element} /></li>);
	if ("ordered" == element.style) {
		return <ol>{elements}</ol>;
	} else {
		return <ul>{elements}</ul>;
	}
};

export const RichTextQuoteElement: React.FunctionComponent<api.RichTextQuoteElement> = (element) => {
	const elements = element.elements.map((element, index) => <RichTextElement key={index} {...element} />);
	const theme = useTheme();
	return (
		<blockquote
			style={{
				borderLeft: `4px solid ${grey.A100}`,
				margin: 0,
				paddingLeft: theme.spacing(2),
			}}
		>{elements}</blockquote>
	);
};

export const RichTextPreformattedElement: React.FunctionComponent<api.RichTextPreformattedElement> = (element) => {
	const elements = element.elements.map((element, index) => <RichTextElement key={index} {...element} />);
	const theme = useTheme();
	return (
		<pre
			style={{
				backgroundColor: grey[200],
				border: STANDARD_BORDER_STYLE,
				borderRadius: "3px",
				marginBottom: theme.spacing(1),
				marginTop: theme.spacing(1),
				padding: theme.spacing(2),
				whiteSpace: "pre-wrap",
			}}
		>{elements}</pre>
	);
};

export const RichTextElement: React.FunctionComponent<api.RichTextElement> = (element) => {
	switch (element.type) {
		case "rich_text_section": return <RichTextSectionElement {...element as api.RichTextSectionElement} />;
		case "rich_text_quote": return <RichTextQuoteElement {...element as api.RichTextQuoteElement} />;
		case "rich_text_preformatted": return <RichTextPreformattedElement {...element as api.RichTextPreformattedElement} />;
		case "text": return <RichTextSectionText {...element as api.RichTextSectionElement.Text} />;
		case "channel": return <RichTextSectionChannel {...element as api.RichTextSectionElement.Channel} />;
		case "user": return <RichTextSectionUser {...element as api.RichTextSectionElement.User} />;
		case "emoji": return <RichTextSectionEmoji {...element as api.RichTextSectionElement.Emoji} />;
		case "link": return <RichTextSectionLink {...element as api.RichTextSectionElement.Link} />;
	}
	return <span>Unknown RichTextElement={element.type}</span>;
};

export const RichTextSectionText: React.FunctionComponent<api.RichTextSectionElement.Text> = (element) => {
	return stylized(<React.Fragment>{newline2br(element.text)}</React.Fragment>, element.style);
};


export const RichTextSectionChannel: React.FunctionComponent<api.RichTextSectionElement.Channel> = (element) => {
	return stylized(<span>{element.channelId}</span>, element.style);
};

export const RichTextSectionUser: React.FunctionComponent<api.RichTextSectionElement.User> = (element) => {
	const { usersMap } = React.useContext(SlackContext);
	const userProfile = usersMap[element.userId]?.profile;
	const userName = api.getDisplayableUserName(userProfile) || element.userId;
	return stylized(<a>@{userName}</a>, element.style);
};

export const RichTextSectionEmoji: React.FunctionComponent<api.RichTextSectionElement.Emoji> = (element) => {
	return stylized(<React.Fragment>{name2emoji(element.name)}</React.Fragment>, element.style);
};

export const RichTextSectionLink: React.FunctionComponent<api.RichTextSectionElement.Link> = (element) => {
	return stylized(<a href={element.url}>{element.text ?? element.url}</a>, element.style);
};

export const ImageElement: React.FunctionComponent<api.ImageElement> = (props) => {
	// Replication from Slack Web UI
	return (
		<div
			aria-label={props.altText}
			role="img"
			style={{
				// Copy from Slack Web
				backgroundSize: "cover",
				backgroundRepeat: "no-repeat",
				backgroundPosition: "50% center",
				borderRadius: "4px",
				backgroundImage: "url(" + props.imageUrl + ")",
			}}
			title={props.altText}
		/>
	);
};

export const isAttachmentSupported = (attachment: api.Attachment): boolean => {
	return null == attachment.blocks?.find((block) => !isLayoutBlockSupported(block))
		&& null == api.ATTACHMENT_LEGACY_FIELDS.find((field) => null != attachment[field]);
};

export const isLayoutBlockSupported = (block: api.LayoutBlock): boolean => {
	switch (block.type) {
		case "context": return isContextBlockSupported(block as api.ContextBlock);
		case "divider": return isDividerBlockSupported(block as api.DividerBlock);
		case "header": return isHeaderBlockSupported(block as api.HeaderBlock);
		case "image": return isImageBlockSupported(block as api.ImageBlock);
		case "rich_text": return isRichTextBlockSupported(block as api.RichTextBlock);
		case "section": return isSectionBlockSupported(block as api.SectionBlock);
	}
	return false;
};
const isContextBlockSupported = (block: api.ContextBlock): boolean => {
	return null == block.elements?.find((e) => !isBlockElementSupported(e));
};

const isDividerBlockSupported = (block: api.DividerBlock): boolean => {
	return true;
};

const isHeaderBlockSupported = (block: api.HeaderBlock): boolean => {
	return isTextObjectSupported(block.text);
};

const isImageBlockSupported = (block: api.ImageBlock): boolean => {
	return true;
};

const isRichTextBlockSupported = (block: api.RichTextBlock): boolean => {
	return null == block.elements?.find((e) => !isBlockElementSupported(e));
};

const isSectionBlockSupported = (block: api.SectionBlock): boolean => {
	return (null == block.text || isTextObjectSupported(block.text))
		&& (null == block.accessory || isBlockElementSupported(block.accessory))
		&& (null == block.fields?.find((f) => !isTextObjectSupported(f)));
};

const isBlockElementSupported = (element: api.BlockElement): boolean => {
	switch (element.type) {
		case "rich_text_section": return isRichTextSectionElementSupported(element as api.RichTextSectionElement);
		case "rich_text_list": return isRichTextListElementSupported(element as api.RichTextListElement);
		case "rich_text_quote": return isRichTextQuoteElementSupported(element as api.RichTextQuoteElement);
		case "rich_text_preformatted": return isRichTextPreformattedElementSupported(element as api.RichTextPreformattedElement);
		case "image": return isImageElementSupported(element as api.ImageElement);
		case "mrkdwn": return isMarkdownTextObjectSupported(element as api.MarkdownTextObject);
	}
	return false;
};

const isTextObjectSupported = (object: api.TextObject): boolean => {
	switch (object?.type) {
		case "mrkdwn": return isMarkdownTextObjectSupported(object as api.MarkdownTextObject);
		case "plain_text": return isPlainTextObjectSupported(object as api.PlainTextObject);
	}
	return false;
};
const isMarkdownTextObjectSupported = (element: api.MarkdownTextObject): boolean => {
	try {
		parse(element.text, SLACK);
		return true;
	} catch (e) {
		return false;
	}
};

const isPlainTextObjectSupported = (element: api.PlainTextObject): boolean => {
	return true;
};

const isRichTextSectionElementSupported = (element: api.RichTextSectionElement): boolean => {
	return null == element.elements?.find((e) => !isRichTextElementSupported(e));
};

const isRichTextListElementSupported = (element: api.RichTextListElement): boolean => {
	return null == element.elements?.find((e) => !isRichTextElementSupported(e));
};

const isRichTextQuoteElementSupported = (element: api.RichTextQuoteElement): boolean => {
	return null == element.elements?.find((e) => !isRichTextElementSupported(e));
};
const isRichTextPreformattedElementSupported = (element: api.RichTextPreformattedElement): boolean => {
	return null == element.elements?.find((e) => !isRichTextElementSupported(e));
};
const isRichTextElementSupported = (element: api.RichTextElement): boolean => {
	switch (element.type) {
		case "rich_text_section": return isRichTextSectionElementSupported(element as api.RichTextSectionElement);
		case "rich_text_quote": return isRichTextQuoteElementSupported(element as api.RichTextQuoteElement);
		case "rich_text_preformatted": return isRichTextPreformattedElementSupported(element as api.RichTextPreformattedElement);
		case "text": return isRichTextSectionTextSupported(element as api.RichTextSectionElement.Text);
		case "channel": return isRichTextSectionChannelSupported(element as api.RichTextSectionElement.Channel);
		case "user": return isRichTextSectionUserSupported(element as api.RichTextSectionElement.User);
		case "emoji": return isRichTextSectionEmojiSupported(element as api.RichTextSectionElement.Emoji);
		case "link": return isRichTextSectionLinkSupported(element as api.RichTextSectionElement.Link);
	}
	return false;
};

const isRichTextSectionTextSupported = (element: api.RichTextSectionElement.Text): boolean => {
	return true;
};

const isRichTextSectionChannelSupported = (element: api.RichTextSectionElement.Channel): boolean => {
	return true;
};

const isRichTextSectionUserSupported = (element: api.RichTextSectionElement.User): boolean => {
	return true;
};

const isRichTextSectionEmojiSupported = (element: api.RichTextSectionElement.Emoji): boolean => {
	return true;
};

const isRichTextSectionLinkSupported = (element: api.RichTextSectionElement.Link): boolean => {
	return true;
};

const isImageElementSupported = (element: api.ImageElement): boolean => {
	return true;
};